import {
  AfterViewChecked,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  Type,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom, Subscription } from 'rxjs';
import { MaterialModule } from 'src/app/material/material.module';
import { UserInfo } from 'src/app/models/user-info.model';
import { ExchangeStatus } from 'src/app/services/graph/graph.service';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { LogService } from 'src/app/services/logs/log.service';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { UsersService } from 'src/app/services/users/users.service';
import { ExchangeListFields } from 'src/app/models/resource-list-fields.model';
import { MatTableDataSource } from '@angular/material/table';
import { entAnimations } from 'src/app/animations';
import { MatPaginator } from '@angular/material/paginator';
import { FormGroup } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';

@Component({
  selector: 'app-exchange-page',
  standalone: true,
  imports: [
    TranslateModule,
    MaterialModule,
    MatStepperModule,
  ],
  templateUrl: './exchange-page.component.html',
  styleUrl: './exchange-page.component.scss',
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
})
export class ExchangePageComponent implements OnInit, OnChanges, AfterViewChecked {
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @Input() exchangeType: 'team' | 'resource' | 'sharedmailbox';
  @Input() isClassTeams?: boolean;
  @Input() url: string;
  @Input() dataSource: MatTableDataSource<ExchangeListFields>;
  @Input() displayedColumns: string[];
  @Input() hiddenColumns: string[];
  @Input() addDialogComponent!: Type<unknown>;
  @Input() deleteDialogComponent!: Type<unknown>;
  @Input() isLoading: boolean;
  @Input() hasNoExchangeItems: boolean;
  @Output() refreshList = new EventEmitter<void>();

  disabledForMaintenance: boolean;
  sortedValue: string;
  currentUser: UserInfo;
  isExchangeItemDisabled = false;
  windowWidth: number = window.innerWidth;
  expandedElement: ExchangeListFields | null;
  readonly exchangeStatus = ExchangeStatus;
  private subscription: Subscription = new Subscription();
  private paginatorInitialized = false;

  constructor(
    private _settingsService: SettingsService,
    private _logService: LogService,
    private _matDialog: MatDialog,
    private _userService: UsersService,
    private _router: Router,
    private _routeStatusService: RouteStatusService,
    private _localStorageService: LocalStorageService
  ) {
    this.currentUser = this._userService.currentUserValue;
  }

  refreshListItems() {
    this.refreshList.emit();
  }

  ngAfterViewChecked() {
    if (!this.paginatorInitialized && this.paginator && this.dataSource) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      this.defaultSorting();
      this.paginatorInitialized = true;
    }
  }

  async ngOnInit() {
    //Maintenance mode settings
    const urls = [this.url];
    this.subscription.add(
      this._routeStatusService
        .getRouteStatusForUrls(urls)
        .subscribe(statuses => {
          this.isExchangeItemDisabled = statuses[this.url];
          this.disabledForMaintenance = statuses[this.url];
        })
    );
  }

  ngOnChanges() {
    if (this.dataSource && this.paginator) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      this.defaultSorting();

      this.paginatorInitialized = false;
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.windowWidth = window.innerWidth;
  }

  get ExchangeType() {
    return this.exchangeType.toUpperCase();
  }

  redirectSingleExchangeItem(id: string) {
    this._router.navigate([this.url + '/' + id]);
    this._localStorageService.setItem('returnUrl', this._router.url);
  }

  createExchangeItem() {
    const dialogRef = this._matDialog.open(this.addDialogComponent, {
      panelClass: 'app-resource-add-dialog',
      data: {
        action: 'new',
        currentUser: this.currentUser,
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.refreshListItems();
    });
  }

  deleteExchangeItem(e) {
    const dialogRef = this._matDialog.open(this.deleteDialogComponent, {
      panelClass: 'app-role-delete-confirm-dialog',
      data: {
        id: e.id,
        name: e.displayName,
      },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((response: FormGroup) => {
      if (response) {
        this.refreshListItems();
      }
    });
  }

  toggleRow(element: ExchangeListFields): void {
    this.expandedElement = this.expandedElement === element ? null : element;
  }

  defaultSorting() {
    const order = [
      'Demande de création',
      'Demande de modification',
      'A jour',
      'En attente',
      'Demande de suppression',
      'Erreur',
    ];

    this.dataSource.data = this.dataSource.data.sort((a, b) => {
      return order.indexOf(a.state) - order.indexOf(b.state);
    });
  }
}
